import Button from '@ui/Button'
import { IconMail } from '@ui/icons'
import ProviderButton from '@ui/ProviderButton'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import {
  completeRedirectSignIn,
  getIdpConfig,
  IdpError,
  type IdpErrorCode,
  isRedirectPending,
  signInWithGoogle,
  startSession,
} from '@/auth/idp'

import LoginCard from './login/LoginCard'
import PasswordLogin, {
  type CheckEmailReason,
  type TotpStep,
} from './login/PasswordLogin'
import TotpCard from './login/TotpCard'

// Every sign-in goes through Identity Platform (Google by redirect via the
// /__/auth proxy, or email + password) and lands on the same JWT cookie. An
// environment without it configured has no way to sign in, and says so.
const Login = () => {
  const { t } = useTranslation()
  const [idpEnabled, setIdpEnabled] = useState(false)
  // Until the config answers we don't know which Google path to take, so the
  // buttons wait rather than risk sending a click down the fallback route.
  const [ready, setReady] = useState(false)
  const [pending, setPending] = useState(isRedirectPending)
  const [totp, setTotp] = useState<TotpStep | null>(null)
  const [lastEmail, setLastEmail] = useState('')
  const [googleError, setGoogleError] = useState<IdpErrorCode | null>(null)
  const [noAccessEmail, setNoAccessEmail] = useState<string | null>(null)
  const [checkEmail, setCheckEmail] = useState<{
    reason: CheckEmailReason
    email: string
  } | null>(null)

  useEffect(() => {
    let live = true
    const settle = async () => {
      const config = await getIdpConfig()
      if (!live) return
      setIdpEnabled(config !== null)
      setReady(true)
      if (!config) {
        setPending(false)
        return
      }
      try {
        const result = await completeRedirectSignIn()
        if (!live) return
        if (result?.kind === 'totp-required') setTotp(result)
        if (result?.kind === 'done') {
          window.location.assign(await startSession(result.user))
          return
        }
      } catch (err) {
        if (!live) return
        if (err instanceof IdpError && err.code === 'no-access') {
          setNoAccessEmail(err.email ?? '')
        } else {
          setGoogleError(err instanceof IdpError ? err.code : 'unknown')
        }
      }
      if (live) setPending(false)
    }
    settle()
    return () => {
      live = false
    }
  }, [])

  const onGoogle = () => {
    setGoogleError(null)
    setPending(true)
    signInWithGoogle().catch((err) => {
      setPending(false)
      setGoogleError(err instanceof IdpError ? err.code : 'unknown')
    })
  }

  if (pending) {
    return (
      <LoginCard title={t('auth.login.title')}>
        <p className="text-sm text-ink-500">{t('auth.logging-in')}</p>
      </LoginCard>
    )
  }

  if (!ready) {
    return (
      <LoginCard
        title={t('auth.login.title')}
        subtitle={t('auth.login.subtitle-invited')}
      >
        <p className="text-sm text-ink-500">{t('common.loading')}</p>
      </LoginCard>
    )
  }

  if (totp) {
    return (
      <TotpCard
        resolver={totp.resolver}
        method={totp.method}
        email={totp.email}
        onBack={() => {
          if (totp.method === 'password') setLastEmail(totp.email ?? '')
          setTotp(null)
        }}
        onNoAccess={(email) => {
          setTotp(null)
          setNoAccessEmail(email)
        }}
      />
    )
  }

  if (!idpEnabled) {
    return (
      <LoginCard title={t('auth.login.title')}>
        <p className="text-sm text-ink-700">{t('auth.login.not-configured')}</p>
      </LoginCard>
    )
  }

  if (noAccessEmail !== null) {
    return (
      <LoginCard title={t('auth.no-access.title')}>
        <p className="text-base text-ink-900">
          <Trans
            i18nKey="auth.no-access.mismatch-body"
            values={{ email: noAccessEmail }}
            components={{ strong: <strong /> }}
          />
        </p>
        <p className="text-sm text-ink-700">
          {t('auth.no-access.mismatch-next', { provider: 'Google' })}
        </p>
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setNoAccessEmail(null)}
        >
          {t('auth.no-access.back')}
        </Button>
      </LoginCard>
    )
  }

  if (checkEmail) {
    return (
      <LoginCard title={t('auth.check-email.title')}>
        <span
          className="w-12 h-12 rounded-full grid place-items-center bg-green-100 text-green-800"
          aria-hidden="true"
        >
          <IconMail size={22} />
        </span>
        <p className="text-base text-ink-900">
          <Trans
            i18nKey={`auth.check-email.${checkEmail.reason}-body`}
            values={{ email: checkEmail.email }}
            components={{ strong: <strong /> }}
          />
        </p>
        {checkEmail.reason === 'verify' && (
          <>
            <p className="text-sm text-ink-700">
              {t('auth.check-email.verify-next')}
            </p>
            <p className="text-sm text-ink-500">
              {t('auth.check-email.not-arrived')}
            </p>
          </>
        )}
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setCheckEmail(null)}
        >
          {t('auth.password.back-to-sign-in')}
        </Button>
      </LoginCard>
    )
  }

  return (
    <LoginCard
      title={t('auth.login.title')}
      subtitle={t('auth.login.subtitle-invited')}
    >
      <ProviderButton provider="google" onClick={onGoogle} />
      {googleError && googleError !== 'cancelled' && (
        <p role="alert" className="text-sm text-flag">
          {t(`auth.password.errors.${googleError}` as const)}
        </p>
      )}

      <div
        className="flex items-center gap-3 text-xs uppercase tracking-caps text-ink-400"
        aria-hidden="true"
      >
        <span className="flex-1 h-px bg-ink-100" />
        {t('auth.login.or')}
        <span className="flex-1 h-px bg-ink-100" />
      </div>
      <PasswordLogin
        initialEmail={lastEmail}
        onTotpRequired={setTotp}
        onCheckEmail={(reason, email) => setCheckEmail({ reason, email })}
      />
    </LoginCard>
  )
}

export default Login
